<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import type { Player } from '@/types'
import SlotItem from './SlotItem.vue'
import { QrcodeStream } from 'vue-qrcode-reader'

interface GuestSlot {
  id: string
  slotId: string
  offerCode: string
  status: 'pending' | 'connected'
  createdAt: number
}

interface CreatingSlot {
  id: string
  status: 'creating'
}

const props = defineProps<{
  players: Player[]
  offerCode: string
  startGame: () => void
  copyCode: () => void | Promise<void>
  createGuestOffer: () => void | Promise<void>
  acceptGuestAnswer: () => void | Promise<void>
  generateQR: (text: string, containerId: string) => void
  myPlayerId?: string
  guestSlots: GuestSlot[]
  creatingSlots: CreatingSlot[]
  copySlotCode: (slotId: string) => void | Promise<void>
  deleteGuestSlot: (slotId: string) => void
  onGuestQRDetect: (detectedCodes: any[]) => void
  onCameraReady: (capabilities: any) => void
  onScannerError: (error: any) => void
  selectedCameraId?: string
  categoryMode: string
  difficulty: string
  roundsPerPlayer: number
  timerDuration: number
  connectionMode?: 'internet' | 'local'
}>()

// Local state for scanner activity
const localScannerActive = ref(false)

// Constraints computadas - usando el formato correcto para vue-qrcode-reader
const cameraConstraints = computed(() => {
  if (props.selectedCameraId) {
    console.log('[LobbyScanner] 🎯 Usando deviceId específico:', props.selectedCameraId.slice(0, 8) + '...')
    return {
      deviceId: { exact: props.selectedCameraId },
      width: { ideal: 320 },
      height: { ideal: 240 }
    }
  }
  console.log('[LobbyScanner] 📷 Sin deviceId, usando facingMode')
  return { facingMode: 'environment' }
})

const handleDetect = (detectedCodes: any[]) => {
  console.log('[LobbyScanner] ✅ QR detectado:', detectedCodes)
  props.onGuestQRDetect(detectedCodes)
}

const handleCameraOn = (capabilities: any) => {
  console.log('[LobbyScanner] 📷 Cámara lista con capabilities:', capabilities)
  props.onCameraReady(capabilities)
}

const handleError = (error: any) => {
  console.error('[LobbyScanner] ❌ Error:', error)
  props.onScannerError(error)
}

// Watcher para detectar cambios en selectedCameraId
watch(() => props.selectedCameraId, (newId) => {
  if (newId) {
    console.log('[LobbyScanner] 📷 CameraId prop cambiado:', newId.slice(0, 8) + '...')
  }
})

// Debug al montar
onMounted(() => {
  console.log('[LobbyScanner] 🚀 Componente montado')
  console.log('[LobbyScanner] 📷 Cámara seleccionada:', props.selectedCameraId?.slice(0, 8) + '...' || 'NINGUNA')
  console.log('[LobbyScanner] 🎯 Constraints actuales:', JSON.stringify(cameraConstraints.value, null, 2))
})

// Función para verificar si es el jugador actual
const isMe = (playerId: string) => {
  return props.myPlayerId && playerId === props.myPlayerId
}

// Función para verificar si es host
const isHost = computed(() => {
  return props.players.some(p => p.isHost && isMe(p.id))
})

const handleCreateGuestOffer = async () => {
  await props.createGuestOffer()
}

const handleAcceptGuestAnswer = async () => {
  const input = document.getElementById('guest-answer-input') as HTMLInputElement
  if (!input || !input.value.trim()) {
    alert('Pega el código del jugador')
    return
  }

  // Guardar el valor temporalmente
  const tempValue = input.value
  try {
    await props.acceptGuestAnswer()
    if (input) input.value = ''
  } catch (e) {
    alert('Error al aceptar jugador: ' + (e as Error).message)
    if (input) input.value = tempValue
  }
}

const toggleScanner = () => {
  localScannerActive.value = !localScannerActive.value
}

// Función para mostrar QR de un slot específico
const showSlotQR = (slot: GuestSlot) => {
  // Crear modal con el mismo estilo que QRModal (fondo blanco)
  const modal = document.createElement('div')
  modal.className = 'fixed inset-0 bg-white flex flex-col items-center justify-center z-50 p-6'
  modal.onclick = () => modal.remove()

  // Botón de cerrar
  const closeButton = document.createElement('button')
  closeButton.className = 'absolute top-4 right-4 text-gray-800 text-3xl hover:scale-110 transition-transform z-10'
  closeButton.textContent = '✕'
  closeButton.onclick = () => modal.remove()
  modal.appendChild(closeButton)

  // Contenedor central
  const content = document.createElement('div')
  content.className = 'text-center'

  // Título
  const title = document.createElement('h2')
  title.className = 'text-2xl font-bold text-gray-800 mb-6'
  title.textContent = 'Código para Jugador'
  content.appendChild(title)

  // Contenedor del QR
  const qrContainer = document.createElement('div')
  qrContainer.id = 'temp-qr-container'
  qrContainer.className = 'flex justify-center items-center bg-white p-6 rounded-2xl'
  content.appendChild(qrContainer)

  // Instrucciones
  const instructions = document.createElement('p')
  instructions.className = 'text-gray-600 mt-6 text-sm'
  instructions.textContent = 'Toca fuera o en ✕ para cerrar'
  content.appendChild(instructions)

  modal.appendChild(content)
  document.body.appendChild(modal)

  // Generar QR después de añadir al DOM
  setTimeout(() => {
    props.generateQR(slot.offerCode, 'temp-qr-container')
  }, 100)
}

defineEmits<{
  'open-room-settings-modal': []
  'open-qr-modal': [code: string]
}>()
</script>

<template>
  <div class="flex flex-col items-center justify-center min-h-screen p-6">
    <div class="glass rounded-3xl p-6 max-w-md w-full animate-fade-in">
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-2xl font-bold text-white">Sala</h2>
        <button @click="$emit('open-room-settings-modal')" class="text-2xl hover:scale-110 transition-transform">⚙️</button>
      </div>

      <div class="mb-6">
        <h3 class="text-teal-200 font-semibold mb-2">Jugadores ({{ players.length }})</h3>
        <div class="space-y-2 max-h-64 overflow-y-auto">
          <!-- Jugadores reales -->
          <div v-for="player in players" :key="player.id" class="flex items-center gap-2 bg-white/10 rounded-lg p-3">
            <span class="text-xl">👤</span>
            <span class="text-white flex-1">{{ player.name }}<span v-if="isMe(player.id)" class="text-teal-200 text-xs ml-1">(tú)</span></span>
            <span
              class="text-xs px-2 py-1 rounded-full"
              :class="{
                'bg-green-500/30 text-green-300': player.connectionStatus === 'connected',
                'bg-yellow-500/30 text-yellow-300': player.connectionStatus === 'pending',
                'bg-red-500/30 text-red-300': player.connectionStatus === 'disconnected'
              }"
            >
              {{ player.latency ? `${player.latency}ms` : (player.connectionStatus === 'connected' ? '✓' : player.connectionStatus === 'pending' ? '⏳' : '✗') }}
            </span>
          </div>

          <!-- Slots pendientes (solo para host) -->
          <SlotItem
            v-for="slot in guestSlots"
            v-show="isHost && slot.status === 'pending'"
            :key="slot.id"
            :slot="slot"
            :onShowQR="showSlotQR"
            :onCopyCode="copySlotCode"
            :onDelete="deleteGuestSlot"
          />

          <!-- Slots creándose (solo para host) -->
          <div
            v-for="slot in creatingSlots"
            v-show="isHost"
            :key="slot.id"
            class="flex items-center gap-2 bg-white/10 rounded-lg p-3 border-2 border-dashed border-white/30"
          >
            <span class="text-xl">⏳</span>
            <span class="text-white flex-1">Creando hueco...</span>
            <div class="spinner"></div>
          </div>

          <!-- Botón (+) para añadir nuevo slot (solo para host) - SIEMPRE AL FINAL -->
          <div
            v-if="isHost"
            class="flex items-center justify-center gap-2 bg-white/5 rounded-lg p-3 border-2 border-dashed border-white/20 cursor-pointer hover:bg-white/10 transition-colors"
            @click="handleCreateGuestOffer"
          >
            <span class="text-2xl text-accent">➕</span>
            <span class="text-accent font-semibold">Añadir Jugador</span>
          </div>
        </div>
      </div>

      <!-- Sección para aceptar respuesta del guest (solo para host, solo en modo local) -->
      <div v-if="isHost && guestSlots.length > 0 && connectionMode === 'local'" class="mb-6 bg-white/5 rounded-xl p-4">
        <h3 class="text-teal-200 font-semibold mb-2 text-sm">Aceptar jugador</h3>
        <p class="text-white/70 text-xs mb-3">Cuando un jugador se una, pega aquí su código</p>

        <!-- QR Scanner with vue-qrcode-reader -->
        <div v-if="localScannerActive && selectedCameraId" class="mb-2 rounded-xl overflow-hidden bg-black">
          <QrcodeStream
            :key="selectedCameraId"
            :constraints="cameraConstraints"
            @detect="handleDetect"
            @camera-on="handleCameraOn"
            @error="handleError"
          />
        </div>

        <div class="flex gap-2 mb-2">
          <button
            @click="toggleScanner"
            :disabled="!selectedCameraId"
            class="flex-1 py-2 bg-secondary text-white font-semibold rounded-lg hover:scale-105 transition-transform text-sm disabled:opacity-50"
          >
            {{ localScannerActive ? '📷 Escaneando...' : '📷 Escanear QR del jugador' }}
          </button>
        </div>
        <div class="flex gap-2">
          <input
            id="guest-answer-input"
            type="text"
            placeholder="O pega el código del jugador..."
            class="flex-1 bg-white/10 text-white rounded-lg px-3 py-2 text-sm border border-white/20 focus:border-accent focus:outline-none"
          >
          <button
            @click="handleAcceptGuestAnswer"
            class="px-4 py-2 bg-accent text-dark font-bold rounded-lg hover:scale-105 transition-transform text-sm"
          >
            ✓
          </button>
        </div>
      </div>

      <!-- Solo el host puede iniciar la partida -->
      <div v-if="isHost">
        <!-- Configuración del Juego -->
        <div class="mb-4 space-y-2">
          <!-- Modo de Categoría -->
          <div class="p-3 bg-white/5 rounded-xl">
            <div class="flex items-center justify-between mb-1">
              <div class="flex items-center gap-2">
                <span class="text-xl">{{ categoryMode === 'roulette' ? '🎰' : '📂' }}</span>
                <span class="text-white font-medium text-sm">
                  {{ categoryMode === 'roulette' ? 'Modo Ruleta' : 'Modo Manual' }}
                </span>
              </div>
            </div>
            <p class="text-white/60 text-xs">
              {{ categoryMode === 'roulette' ? 'Aleatorio con ruleta' : 'Categoría específica' }}
            </p>
          </div>

          <!-- Dificultad -->
          <div class="p-3 bg-white/5 rounded-xl">
            <div class="flex items-center justify-between mb-1">
              <div class="flex items-center gap-2">
                <span class="text-xl">🎯</span>
                <span class="text-white font-medium text-sm">Dificultad</span>
              </div>
              <span class="text-accent font-bold text-sm">{{ difficulty === 'fácil' ? 'Fácil' : difficulty === 'medio' ? 'Medio' : 'Difícil' }}</span>
            </div>
            <p class="text-white/60 text-xs">
              {{ difficulty === 'fácil' ? 'Pistas directas' : difficulty === 'medio' ? 'Pistas moderadas' : 'Pistas abstractas' }}
            </p>
          </div>

          <!-- Rondas -->
          <div class="p-3 bg-white/5 rounded-xl">
            <div class="flex items-center justify-between mb-1">
              <div class="flex items-center gap-2">
                <span class="text-xl">🔢</span>
                <span class="text-white font-medium text-sm">Rondas por jugador</span>
              </div>
              <span class="text-accent font-bold text-sm">{{ roundsPerPlayer }}</span>
            </div>
          </div>

          <!-- Duración -->
          <div class="p-3 bg-white/5 rounded-xl">
            <div class="flex items-center justify-between mb-1">
              <div class="flex items-center gap-2">
                <span class="text-xl">⏱️</span>
                <span class="text-white font-medium text-sm">Duración por turno</span>
              </div>
              <span class="text-accent font-bold text-sm">{{ timerDuration }}s</span>
            </div>
          </div>
        </div>

        <button
          @click="startGame"
          :disabled="players.length < 2"
          class="w-full py-4 px-6 bg-accent text-dark font-bold rounded-xl text-lg hover:scale-105 transition-transform disabled:opacity-50"
        >
          🎯 {{ categoryMode === 'roulette' ? 'Girar Ruleta' : 'Iniciar Partida' }}
        </button>
      </div>

      <!-- Los guests ven un mensaje de espera -->
      <div v-else class="w-full py-4 px-6 bg-white/10 rounded-xl text-center">
        <p class="text-white text-lg">⏳ Esperando al host para iniciar la partida...</p>
        <p class="text-white/60 text-sm mt-1">{{ players.length }} {{ players.length === 1 ? 'jugador conectado' : 'jugadores conectados' }}</p>
      </div>
    </div>
  </div>
</template>
